<template>
  <DrupalLink
    v-slot="{ isExternal, icon }"
    :to="to"
    :aria-label="linkLabel"
    :class="['link-list-item', $attrs.class]"
  >
    <SpriteSymbol
      :name="icon"
      class="mr-[0.5em] mt-[0.25em] size-[1em] shrink-0"
      aria-hidden="true"
    />
    <span class="mr-auto">
      {{ title || to }}
    </span>
    <SpriteSymbol
      name="arrow-right"
      class="ml-4 size-[1.5em] shrink-0"
      :class="{ '-rotate-45': isExternal }"
      aria-hidden="true"
    />
  </DrupalLink>
</template>

<script lang="ts" setup>
const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  doc: 'DOC',
  docx: 'DOCX',
  xls: 'XLS',
  xlsx: 'XLSX',
  ppt: 'PPT',
  pptx: 'PPTX',
  csv: 'CSV',
  zip: 'ZIP',
}

const props = defineProps<{
  to: string
  title: string
  filename?: string
}>()

defineOptions({
  inheritAttrs: false,
})

function extensionOf(value: string): string | undefined {
  const path = value.split(/[?#]/)[0] || ''
  return /\.([a-z0-9]+)$/i.exec(path)?.[1]?.toLowerCase()
}

const fileType = computed(() => {
  // Media files are usually served under a clean alias (e.g.
  // /de/dokumente/merkblatt), so the extension can't be read from the URL.
  // Fall back to it only when the real filename isn't known.
  const extension = props.filename
    ? extensionOf(props.filename)
    : extensionOf(props.to || '')
  return extension ? FILE_TYPE_LABELS[extension] : undefined
})

const linkLabel = computed(() => {
  const label = props.title || props.to
  return fileType.value ? `${label} (${fileType.value})` : label
})
</script>

<style lang="postcss">
.link-list-item {
  /* focus:focusable */
  @apply flex border-b border-b-gray-300 py-20 font-semibold tracking-wide text-blue-700 first:border-t first:border-t-gray-300 hover:bg-gray-100 hover:text-blue-900 lg:text-xl;

  .bg-highlight & {
    @apply text-white hover:bg-blue-900 hover:text-gray-400;
  }
}
</style>
